import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, Upload, Check, Loader2, AlertTriangle } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { getLastPositionWorkspacePage } from '../hooks/usePageTracking'
import {
  buildPositionWorkspaceTarget,
  canonicalizePositionWorkspaceRoute,
} from '../lib/positionWorkspaceRoute'
import AccountName from './AccountName'
import PrivateValue from './PrivateValue'

const fmtCurrency = (val) => {
  if (val == null) return '\u2014'
  return Number(val).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export function buildImportWorkspaceTarget(importResults, basePage = null) {
  const results = Array.isArray(importResults) ? importResults : []
  const firstSuccess = results.find(r => r?.id && !r.error)
  if (!firstSuccess) return null

  const symbol = String(firstSuccess.symbol || '').trim().toUpperCase()
  const canonicalBase = canonicalizePositionWorkspaceRoute(basePage)
  const baseParams = new URLSearchParams(canonicalBase.search)
  return buildPositionWorkspaceTarget({
    symbol,
    projectId: String(firstSuccess.id),
    timeframe: baseParams.get('timeframe'),
  })
}

export function getImportConflict(ideas, selectedIdeas, collisionChoices) {
  const targets = new Set()
  for (const idx of selectedIdeas) {
    const idea = ideas[idx]
    const choice = collisionChoices[idx]
    if (choice === 'skip') continue
    if (choice !== 'replace' && idea?.collision_type !== 'active') continue
    const id = idea?.existing_position?.id
    if (!id) continue
    if (targets.has(id)) {
      return `${idea.symbol}: multiple rows target the same existing setup. Keep one selected replacement; choose Add as Separate Idea where available, or deselect the other row.`
    }
    targets.add(id)
  }
  return null
}

export default function SetupImportModal({ open, onClose, onImportComplete }) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const fileRef = useRef(null)

  const [step, setStep] = useState('upload') // upload | parsing | review | importing | done
  const [file, setFile] = useState(null)
  const [dragOver, setDragOver] = useState(false)
  const [error, setError] = useState(null)
  const [parsedData, setParsedData] = useState(null)
  const [selectedIdeas, setSelectedIdeas] = useState(new Set())
  const [selectedAccounts, setSelectedAccounts] = useState({}) // { ideaIndex: Set<account_number> }
  // Collision resolution — per-row user choice for ideas whose symbol
  // already has a non-closed position_project. Keys are the idea index;
  // values are one of: 'replace' | 'add_new' | 'skip'. Active positions
  // default to 'add_tranche' (their historical ADD TRANCHE branch).
  const [collisionChoices, setCollisionChoices] = useState({})
  const [importResults, setImportResults] = useState(null)
  const importConflict = getImportConflict(parsedData?.ideas || [], selectedIdeas, collisionChoices)

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setStep('upload')
      setFile(null)
      setError(null)
      setParsedData(null)
      setSelectedIdeas(new Set())
      setSelectedAccounts({})
      setCollisionChoices({})
      setImportResults(null)
    }
  }, [open])

  const handleFile = useCallback(async (f) => {
    if (!f) return
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp']
    if (!validTypes.includes(f.type)) {
      setError('Please upload a PNG, JPG, or WebP image.')
      return
    }
    setFile(f)
    setError(null)
    setStep('parsing')

    try {
      // Read file as base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result.split(',')[1])
        reader.onerror = reject
        reader.readAsDataURL(f)
      })

      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('Not authenticated')

      const res = await fetch('/api/setups/parse-screenshot', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          image: base64,
          mediaType: f.type === 'image/jpg' ? 'image/jpeg' : f.type,
        }),
      })

      const data = await res.json()
      if (!res.ok) throw new Error(data.error || `API error ${res.status}`)

      setParsedData(data)
      // Select all ideas by default
      const allIdxs = new Set(data.ideas.map((_, i) => i))
      setSelectedIdeas(allIdxs)
      // Select all accounts for each idea
      const acctSel = {}
      const defaultChoices = {}
      data.ideas.forEach((idea, i) => {
        acctSel[i] = new Set(idea.account_sizing.map(a => a.account_number))
        // Default collision resolution: active → add_tranche (legacy), else
        // 'replace' (keep same position_project id, update setup fields).
        if (idea.collision_type === 'active') {
          defaultChoices[i] = 'add_tranche'
        } else if (idea.collision_type) {
          defaultChoices[i] = 'replace'
        }
      })
      setSelectedAccounts(acctSel)
      setCollisionChoices(defaultChoices)
      setStep('review')
    } catch (err) {
      setError(err.message)
      setStep('upload')
    }
  }, [])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    setDragOver(false)
    const f = e.dataTransfer.files[0]
    handleFile(f)
  }, [handleFile])

  // Clipboard paste support — listen for Cmd+V / Ctrl+V when in upload step
  useEffect(() => {
    if (!open || step !== 'upload') return
    const handlePaste = (e) => {
      const items = e.clipboardData?.items
      if (!items) return
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault()
          const f = item.getAsFile()
          if (f) handleFile(f)
          return
        }
      }
    }
    document.addEventListener('paste', handlePaste)
    return () => document.removeEventListener('paste', handlePaste)
  }, [open, step, handleFile])

  const toggleIdea = (idx) => {
    setSelectedIdeas(prev => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })
  }

  const toggleIdeaAccount = (ideaIdx, accountNumber) => {
    setSelectedAccounts(prev => {
      const next = { ...prev }
      const set = new Set(next[ideaIdx] || [])
      if (set.has(accountNumber)) set.delete(accountNumber)
      else set.add(accountNumber)
      next[ideaIdx] = set
      return next
    })
  }

  const setCollisionChoice = (ideaIdx, choice) => {
    setCollisionChoices(prev => ({ ...prev, [ideaIdx]: choice }))
    if (choice === 'skip') {
      // Also deselect the idea so it's not included in the import count.
      setSelectedIdeas(prev => {
        const next = new Set(prev)
        next.delete(ideaIdx)
        return next
      })
    } else {
      // Re-select if it was deselected earlier.
      setSelectedIdeas(prev => {
        const next = new Set(prev)
        next.add(ideaIdx)
        return next
      })
    }
  }

  const handleImport = async () => {
    if (!parsedData || !user) return
    if (importConflict) {
      setError(importConflict)
      return
    }
    setError(null)
    setStep('importing')

    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('Not authenticated')

      const results = []
      const positionIds = []

      for (const idx of selectedIdeas) {
        const idea = parsedData.ideas[idx]
        const acctSet = selectedAccounts[idx] || new Set()
        const sizing = idea.account_sizing.filter(a => acctSet.has(a.account_number))
        const choice = collisionChoices[idx]

        // Skip explicitly — the row is in the selected set for display but
        // the user chose to skip the import.
        if (choice === 'skip') continue

        // ── ACTIVE: historical ADD TRANCHE branch (unchanged) ──────────
        if (idea.collision_type === 'active') {
          const existingId = idea.existing_position?.id
          if (existingId) {
            await supabase
              .from('position_projects')
              .update({
                trigger_price: idea.trigger_price,
                pattern_failure_price: idea.pattern_failure_price,
                sell_target_price: idea.sell_target_price,
                setup_pattern: idea.pattern,
                entry_type: idea.entry_type,
                loss_at_exit_pct: idea.loss_at_exit_pct,
                recommended_size_pct: idea.recommended_size_pct,
                setup_source: parsedData.source,
                direction: idea.direction === 'Short' ? 'SHORT' : 'LONG',
                updated_at: new Date().toISOString(),
              })
              .eq('id', existingId)
            results.push({ symbol: idea.symbol, action: 'ADD_TRANCHE', id: existingId })
            positionIds.push(existingId)
          }
          continue
        }

        // ── WORKING / IDEA / PLANNED collision: REPLACE in place ───────
        // Update the existing position_project's setup fields. Stage and
        // id are preserved so the Position Board / Switcher / Workspace
        // keep their references. Orders at Schwab are NOT touched — user
        // can update them from the WorkingOrdersPanel if needed.
        if (choice === 'replace' && idea.existing_position?.id) {
          const existingId = idea.existing_position.id
          const { error: updateErr } = await supabase
            .from('position_projects')
            .update({
              trigger_price: idea.trigger_price,
              pattern_failure_price: idea.pattern_failure_price,
              sell_target_price: idea.sell_target_price,
              setup_pattern: idea.pattern,
              entry_type: idea.entry_type,
              loss_at_exit_pct: idea.loss_at_exit_pct,
              recommended_size_pct: idea.recommended_size_pct,
              setup_source: parsedData.source,
              direction: idea.direction === 'Short' ? 'SHORT' : 'LONG',
              account_sizing: sizing.map(s => ({
                account_number: s.account_number,
                shares: s.shares,
                max_loss: s.dollarRisk,
              })),
              updated_at: new Date().toISOString(),
            })
            .eq('id', existingId)

          if (updateErr) {
            results.push({ symbol: idea.symbol, action: 'REPLACE', error: updateErr.message })
          } else {
            results.push({ symbol: idea.symbol, action: 'REPLACE', id: existingId, stage: idea.collision_type })
            positionIds.push(existingId)
          }
          continue
        }

        // ── NEW (fresh) OR "Add as Separate Idea" over a collision ─────
        const { data: created, error: createErr } = await supabase
          .from('position_projects')
          .insert({
            user_id: user.id,
            symbol: idea.symbol,
            stage: 'idea',
            entry_price: idea.trigger_price,
            stop_price: idea.pattern_failure_price,
            target_price: idea.sell_target_price,
            setup_pattern: idea.pattern,
            entry_type: idea.entry_type,
            trigger_price: idea.trigger_price,
            pattern_failure_price: idea.pattern_failure_price,
            loss_at_exit_pct: idea.loss_at_exit_pct,
            sell_target_price: idea.sell_target_price,
            recommended_size_pct: idea.recommended_size_pct,
            setup_source: parsedData.source,
            direction: idea.direction === 'Short' ? 'SHORT' : 'LONG',
            allocation_pct: idea.recommended_size_pct,
            account_sizing: sizing.map(s => ({
              account_number: s.account_number,
              shares: s.shares,
              max_loss: s.dollarRisk,
            })),
            is_draft: false,
          })
          .select('id')
          .single()

        if (createErr) {
          results.push({ symbol: idea.symbol, action: 'NEW', error: createErr.message })
        } else {
          results.push({ symbol: idea.symbol, action: 'NEW', id: created.id })
          positionIds.push(created.id)
        }
      }

      // Update import record with created position IDs
      if (parsedData.import_id && positionIds.length > 0) {
        await supabase
          .from('setup_imports')
          .update({ positions_created: positionIds, status: 'imported' })
          .eq('id', parsedData.import_id)
      }

      setImportResults(results)
      setStep('done')
    } catch (err) {
      setError(err.message)
      setStep('review')
    }
  }

  const handleDone = () => {
    onClose()
    if (onImportComplete) onImportComplete()
    const target = buildImportWorkspaceTarget(importResults, getLastPositionWorkspacePage())
    if (target) {
      navigate(target, { state: { fromImport: true } })
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-canvas border border-border rounded-xl shadow-2xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div>
            <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted">AGI Portfolio</div>
            <div className="font-black text-lg uppercase tracking-wide text-ink">Import Setups</div>
          </div>
          <button onClick={onClose} className="text-inkMuted hover:text-ink transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">

          {/* Upload Step */}
          {step === 'upload' && (
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileRef.current?.click()}
              className={`border-2 border-dashed rounded-xl py-16 text-center cursor-pointer transition-all ${
                dragOver ? 'border-ink bg-canvas2/50' : 'border-border hover:border-ink/40 hover:bg-canvas2/20'
              }`}
            >
              <Upload size={32} className="mx-auto text-inkMuted mb-3" />
              <div className="font-mono text-sm text-ink mb-1">
                Drop AGI Portfolio screenshot here
              </div>
              <div className="font-mono text-xs text-inkMuted">
                drop, click, or paste (⌘V) — PNG, JPG, WebP
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => handleFile(e.target.files[0])}
              />
            </div>
          )}

          {/* Parsing Step */}
          {step === 'parsing' && (
            <div className="py-16 text-center">
              <Loader2 size={32} className="mx-auto text-inkMuted mb-3 animate-spin" />
              <div className="font-mono text-sm text-ink mb-1">Parsing AGI Portfolio...</div>
              <div className="font-mono text-xs text-inkMuted">
                Analyzing screenshot with Claude Vision
              </div>
            </div>
          )}

          {/* Review Step */}
          {step === 'review' && parsedData && (
            <div className="space-y-4">
              {/* Portfolio Summary */}
              {parsedData.portfolio_summary && (
                <div className="flex flex-wrap gap-3">
                  {parsedData.portfolio_summary.portfolio_exposure && (
                    <span className="font-mono text-[10px] px-2 py-0.5 rounded-full bg-canvas2 text-inkMuted border border-border">
                      Exposure: {parsedData.portfolio_summary.portfolio_exposure}
                    </span>
                  )}
                  {parsedData.portfolio_summary.ideal_exposure && (
                    <span className="font-mono text-[10px] px-2 py-0.5 rounded-full bg-canvas2 text-inkMuted border border-border">
                      Ideal: {parsedData.portfolio_summary.ideal_exposure}
                    </span>
                  )}
                  {parsedData.portfolio_summary.nasdaq_trend && (
                    <span className="font-mono text-[10px] px-2 py-0.5 rounded-full bg-canvas2 text-inkMuted border border-border">
                      NASDAQ: {parsedData.portfolio_summary.nasdaq_trend}
                    </span>
                  )}
                </div>
              )}

              {/* Ideas Table */}
              <div className="overflow-x-auto">
                <table className="w-full font-mono text-xs">
                  <thead>
                    <tr className="text-[10px] tracking-widest uppercase text-inkMuted border-b border-border">
                      <th className="text-left py-2 w-6"></th>
                      <th className="text-left py-2">Symbol</th>
                      <th className="text-left py-2">Pattern</th>
                      <th className="text-left py-2">Entry</th>
                      <th className="text-right py-2">Trigger</th>
                      <th className="text-right py-2">Stop</th>
                      <th className="text-right py-2">Target</th>
                      <th className="text-right py-2">Size %</th>
                      <th className="text-right py-2">Shares</th>
                      <th className="text-center py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedData.ideas.map((idea, idx) => {
                      const isSelected = selectedIdeas.has(idx)
                      const totalShares = idea.account_sizing
                        .filter(a => (selectedAccounts[idx] || new Set()).has(a.account_number))
                        .reduce((sum, a) => sum + a.shares, 0)

                      return (
                        <tr
                          key={idx}
                          className={`border-b border-border/50 cursor-pointer hover:bg-canvas2/30 transition-colors ${!isSelected ? 'opacity-40' : ''}`}
                          onClick={() => toggleIdea(idx)}
                        >
                          <td className="py-2.5">
                            <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition-colors ${
                              isSelected ? 'bg-[#c4953a] border-[#c4953a]' : 'border-border bg-canvas'
                            }`}>
                              {isSelected && <Check size={10} className="text-white" strokeWidth={3} />}
                            </div>
                          </td>
                          <td className="py-2.5">
                            <span className="font-black text-sm text-ink">{idea.symbol}</span>
                          </td>
                          <td className="py-2.5 text-inkMuted max-w-[140px] truncate">{idea.pattern}</td>
                          <td className="py-2.5">
                            <span className={`font-mono text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${
                              idea.entry_type === 'On Strength'
                                ? 'bg-forest/10 text-forest border border-forest/30'
                                : 'bg-[#c4953a]/10 text-[#c4953a] border border-[#c4953a]/30'
                            }`}>
                              {idea.entry_type}
                            </span>
                          </td>
                          <td className="py-2.5 text-right text-ink">
                            <PrivateValue>{fmtCurrency(idea.trigger_price)}</PrivateValue>
                          </td>
                          <td className="py-2.5 text-right text-ember">
                            <PrivateValue>{fmtCurrency(idea.pattern_failure_price)}</PrivateValue>
                          </td>
                          <td className="py-2.5 text-right text-forest">
                            <PrivateValue>{fmtCurrency(idea.sell_target_price)}</PrivateValue>
                          </td>
                          <td className="py-2.5 text-right text-inkMuted">{idea.recommended_size_pct}%</td>
                          <td className="py-2.5 text-right text-ink">
                            <PrivateValue>{totalShares}</PrivateValue>
                          </td>
                          <td className="py-2.5 text-center">
                            {idea.collision_type === 'active' ? (
                              <span className="font-mono text-[9px] px-1.5 py-0.5 rounded-full font-semibold bg-[#c4953a]/10 text-[#c4953a] border border-[#c4953a]/30">
                                ADD TRANCHE
                              </span>
                            ) : idea.collision_type === 'working' ? (
                              <span className="font-mono text-[9px] px-1.5 py-0.5 rounded-full font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/30">
                                WORKING
                              </span>
                            ) : idea.collision_type === 'idea' ? (
                              <span className="font-mono text-[9px] px-1.5 py-0.5 rounded-full font-semibold bg-blue-500/10 text-blue-400 border border-blue-400/30">
                                EXISTING IDEA
                              </span>
                            ) : idea.collision_type === 'planned' ? (
                              <span className="font-mono text-[9px] px-1.5 py-0.5 rounded-full font-semibold bg-purple-500/10 text-purple-400 border border-purple-400/30">
                                PLANNED
                              </span>
                            ) : (
                              <span className="font-mono text-[9px] px-1.5 py-0.5 rounded-full font-semibold bg-forest/10 text-forest border border-forest/30">
                                NEW POSITION
                              </span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* Per-idea collision resolution + account selection */}
              {parsedData.ideas.map((idea, idx) => {
                if (!selectedIdeas.has(idx) && collisionChoices[idx] !== 'skip') return null
                const acctSet = selectedAccounts[idx] || new Set()
                const choice = collisionChoices[idx]
                const existing = idea.existing_position
                const showCollision = idea.collision_type && idea.collision_type !== 'active'
                return (
                  <div key={idx} className="bg-canvas2/30 border border-border rounded-lg px-3 py-2">
                    {showCollision && existing && (
                      <div className={`mb-2 px-2 py-1.5 rounded-lg border text-xs ${
                        idea.collision_type === 'working'
                          ? 'bg-amber-500/10 border-amber-500/20'
                          : idea.collision_type === 'idea'
                            ? 'bg-blue-500/10 border-blue-500/20'
                            : 'bg-purple-500/10 border-purple-500/20'
                      }`}>
                        <div className={`text-[9px] font-mono tracking-widest uppercase mb-1 ${
                          idea.collision_type === 'working' ? 'text-amber-400'
                          : idea.collision_type === 'idea' ? 'text-blue-400'
                          : 'text-purple-400'
                        }`}>
                          {idea.collision_type === 'working' ? 'Working Position Detected' : idea.collision_type === 'idea' ? 'Existing Idea' : 'Planned Position'}
                        </div>
                        <div className="text-inkMuted mb-2">
                          {idea.symbol} already has a {idea.collision_type} setup
                          {existing.setup_pattern ? ` (${existing.setup_pattern})` : ''}
                          {idea.collision_type === 'working' ? ' with live orders at Schwab.' : '.'}
                          {' '}Stage + id will be preserved.
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <button
                            onClick={(e) => { e.stopPropagation(); setCollisionChoice(idx, 'replace') }}
                            className={`text-[10px] font-mono px-3 py-1.5 rounded border transition-colors ${
                              choice === 'replace'
                                ? idea.collision_type === 'working'
                                    ? 'bg-amber-500/20 text-amber-400 border-amber-500/40'
                                    : 'bg-blue-500/20 text-blue-400 border-blue-500/40'
                                : 'bg-canvas2 text-inkMuted border-border hover:text-ink'
                            }`}
                          >
                            {idea.collision_type === 'working'
                              ? 'Replace Setup (keep orders, update levels)'
                              : 'Replace Setup'}
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setCollisionChoice(idx, 'add_new') }}
                            className={`text-[10px] font-mono px-3 py-1.5 rounded border transition-colors ${
                              choice === 'add_new'
                                ? 'bg-canvas2 text-ink border-ink/40'
                                : 'bg-canvas2 text-inkMuted border-border hover:text-ink'
                            }`}
                          >
                            Add as Separate Idea
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setCollisionChoice(idx, 'skip') }}
                            className={`text-[10px] font-mono px-3 py-1.5 rounded border transition-colors ${
                              choice === 'skip'
                                ? 'bg-ember/10 text-ember border-ember/30'
                                : 'text-inkDim hover:text-ink border-transparent'
                            }`}
                          >
                            Skip
                          </button>
                        </div>
                      </div>
                    )}
                    <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1.5">
                      {idea.symbol} — Accounts
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {idea.account_sizing.map(acct => {
                        const isOn = acctSet.has(acct.account_number)
                        return (
                          <button
                            key={acct.account_number}
                            onClick={(e) => { e.stopPropagation(); toggleIdeaAccount(idx, acct.account_number) }}
                            className={`font-mono text-[10px] px-2 py-1 rounded-full border transition-colors ${
                              isOn
                                ? 'bg-canvas2 border-ink/30 text-ink'
                                : 'bg-canvas border-border text-inkMuted opacity-50'
                            }`}
                          >
                            <AccountName accountNumber={acct.account_number} className="font-mono text-[10px]" />
                            {' '}
                            <PrivateValue>({acct.shares})</PrivateValue>
                            {acct.wasRiskAdjusted && (
                              <span className="text-[8px] text-ember ml-1" title="Risk-adjusted: reduced from allocation-based sizing">*</span>
                            )}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Importing Step */}
          {step === 'importing' && (
            <div className="py-16 text-center">
              <Loader2 size={32} className="mx-auto text-inkMuted mb-3 animate-spin" />
              <div className="font-mono text-sm text-ink">Creating positions...</div>
            </div>
          )}

          {/* Done Step */}
          {step === 'done' && importResults && (
            <div className="space-y-3 py-4">
              <div className="text-center mb-4">
                <Check size={32} className="mx-auto text-forest mb-2" />
                <div className="font-mono text-sm text-ink">Import Complete</div>
              </div>
              {importResults.map((r, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-border/50">
                  <span className="font-black text-sm text-ink">{r.symbol}</span>
                  <div className="flex items-center gap-2">
                    {r.error ? (
                      <span className="font-mono text-[10px] text-ember">{r.error}</span>
                    ) : (
                      <span className={`font-mono text-[9px] px-2 py-0.5 rounded-full font-semibold ${
                        r.action === 'ADD_TRANCHE'
                          ? 'bg-[#c4953a]/10 text-[#c4953a] border border-[#c4953a]/30'
                          : r.action === 'REPLACE'
                          ? 'bg-blue-500/10 text-blue-400 border border-blue-400/30'
                          : 'bg-forest/10 text-forest border border-forest/30'
                      }`}>
                        {r.action === 'ADD_TRANCHE' ? 'Updated'
                          : r.action === 'REPLACE' ? `Replaced (${r.stage || 'existing'})`
                          : 'Created'}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Error display */}
          {(error || importConflict) && (
            <div className="mt-4 flex items-start gap-2 bg-ember/5 border border-ember/20 rounded-lg px-4 py-3">
              <AlertTriangle size={14} className="text-ember shrink-0 mt-0.5" />
              <div role="alert" className="font-mono text-xs text-ember">{error || importConflict}</div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex items-center justify-between shrink-0">
          <div className="font-mono text-[10px] text-inkMuted">
            {step === 'review' && `${selectedIdeas.size} of ${parsedData?.ideas?.length || 0} ideas selected`}
            {step === 'done' && `${importResults?.filter(r => !r.error).length || 0} positions imported`}
          </div>
          <div className="flex items-center gap-3">
            {step === 'review' && (
              <>
                <button
                  onClick={onClose}
                  className="font-mono text-xs px-4 py-2 rounded-lg border border-border text-inkMuted hover:text-ink transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleImport}
                  disabled={selectedIdeas.size === 0 || Boolean(importConflict)}
                  className="font-mono text-xs font-semibold px-4 py-2 rounded-lg border border-[#c4953a] bg-[#c4953a]/10 text-[#c4953a] hover:bg-[#c4953a]/20 transition-colors disabled:opacity-40"
                >
                  Import {selectedIdeas.size} Selected
                </button>
              </>
            )}
            {step === 'done' && (
              <button
                onClick={handleDone}
                className="font-mono text-xs font-semibold px-4 py-2 rounded-lg border border-forest bg-forest/10 text-forest hover:bg-forest/20 transition-colors"
              >
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
